import MIcon from '../../components/common/MIcon';
import React, { useEffect, useState, useCallback } from 'react';
import { useT } from '../../context/TenantContext';
import { useParams, useNavigate } from 'react-router-dom';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui';
import WatcherEmailsField from '../../components/common/WatcherEmailsField';
import { useRealtime } from '../../hooks/useRealtime';
import DevicePanel    from '../../components/common/DevicePanel';
// Oculto temporalmente en todo el portal — poner a true para reactivarlo
const SHOW_DEVICE_INFO = false;
import AttachGallery from '../../components/common/AttachGallery';
import { StatusChip, PrioChip, LevelChip, STATUS_LABEL as STATUS_MAP_LABEL, PRIO_LABEL as PRIO_MAP_LABEL } from '../../components/common/Chip';

// Kept for showToast messages and escalation progress bar
// STATUS_MAP and PRIO_MAP moved inside component to access t()
const PRIO_MAP_STATIC = { low:'low', medium:'medium', high:'high', critical:'critical' };
const LEVEL_MAP = {
  1:{color:'#1558D6',bg:'#E8F0FE'}, 2:{color:'#6750A4',bg:'#E8DEF8'},
  3:{color:'#E37400',bg:'#FFF3E0'}, 4:{color:'#D93025',bg:'#FFEBEE'},
  5:{color:'#6B7280',bg:'#F3F4F6'},
};
function Card({ title, children, noPad }) {
  return (
    <div style={{background:'#fff',borderRadius:12,overflow:'hidden'}}>
      {title && (
        <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',background:'#FAFAFA'}}>
          <span style={{fontSize:12,fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:.5}}>{title}</span>
        </div>
      )}
      <div style={noPad ? {} : {padding:18}}>{children}</div>
    </div>
  );
}

export default function TicketDetail() {
  const { t } = useT();
  const STATUS_MAP = { open: t('status.open'), in_progress: t('status.in_progress'), escalated: t('ticket.escalate'), scheduled: 'Agendando', resolved: t('status.resolved'), forwarded: 'Derivado' };
  const PRIO_MAP   = { low: t('priority.low'), medium: t('priority.medium'), high: t('priority.high'), critical: t('priority.critical') };
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { agent } = useAuth();
  const [ticket,         setTicket]         = useState(null);
  const [comments,       setComments]       = useState([]);
  const [departments,    setDepartments]    = useState([]);
  const [agents,         setAgents]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [comment,        setComment]        = useState('');
  const [isInternal,     setIsInternal]     = useState(false);
  const [sending,        setSending]        = useState(false);
  const [showEscalate,   setShowEscalate]   = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalateLevel,  setEscalateLevel]  = useState(null);
  const [showForward,    setShowForward]    = useState(false);
  const [forwardDeptId,  setForwardDeptId]  = useState('');
  const [forwardReason,  setForwardReason]  = useState('');
  const [showSchedule,   setShowSchedule]   = useState(false);
  const [schedForm,      setSchedForm]      = useState({ date:'', startTime:'09:00', endTime:'10:00', description:'', color:'var(--una-blue)' });
  const [schedSaving,    setSchedSaving]    = useState(false);
  const [deptMenuOpen,   setDeptMenuOpen]   = useState(false);
  const [activeTab,      setActiveTab]      = useState('comments');
  const [attachFiles,    setAttachFiles]    = useState([]);
  const [attachments,    setAttachments]    = useState([]);
  const [toast,          setToast]          = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm,    setContactForm]    = useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }
  async function load() {
    const [t, c, a, att, depts] = await Promise.all([api.getTicket(id), api.getComments(id), api.getAgents(), api.getAttachments(id), api.getDepartments()]);
    setTicket(t); setComments(c); setAgents(a); setAttachments(att || []); setDepartments((depts || []).filter(d => d.active));
  }
  useEffect(() => { setAttachments([]); load().finally(() => setLoading(false)); }, [id]);

  // Si otro agente actualiza/comenta este mismo ticket mientras lo tienes
  // abierto, refrescar en vez de quedarte con datos desactualizados.
  useRealtime(useCallback((event) => {
    const ticketId = event.ticket?.id ?? event.ticketId;
    if (Number(ticketId) !== Number(id)) return;
    if (['ticket_updated','ticket_escalated','comment_added'].includes(event.type)) load();
  }, [id]));

  async function sendComment() {
    if (!comment.trim()) return;
    setSending(true);
    try {
      const nc = await api.addComment(id, { body: comment, author_name: agent.full_name || agent.username, is_internal: isInternal });
      // Upload attachments linked to this comment
      if (attachFiles.length > 0) {
        const fd = new FormData();
        attachFiles.forEach(f => fd.append('files', f));
        fd.append('comment_id', nc.id);
        await api.uploadAttachments(id, fd);
        setAttachFiles([]);
        const att = await api.getAttachments(id);
        setAttachments(att || []);
      }
      setComments(prev => [...prev, nc]);
      setComment('');
      showToast(isInternal ? t('ticket.internal') : t('ticket.public'));
    } finally { setSending(false); }
  }
  async function changeStatus(s) {
    const updated = await api.updateTicket(id, { status: s });
    setTicket(updated);
    showToast('Estado cambiado a ' + (STATUS_MAP[s] || s));
  }
  async function changePriority(p) {
    const updated = await api.updateTicket(id, { priority: p });
    setTicket(updated);
    showToast('Prioridad cambiada a ' + (PRIO_MAP[p] || p));
  }
  async function saveContactInfo(fields) {
    const updated = await api.updateTicket(id, fields);
    setTicket(updated);
    setEditingContact(false);
    showToast('Datos del ticket actualizados');
  }
  async function handleDelete() {
    if (!confirm(`¿Eliminar el ticket #${ticket.id} permanentemente? Esta acción no se puede deshacer.`)) return;
    try {
      await api.deleteTicket(id);
      navigate('/backdesk/tickets');
    } catch(e) {
      alert(e.message || 'Error al eliminar el ticket');
    }
  }

  async function assign(agentId) {
    const updated = await api.updateTicket(id, { assignee_id: agentId ? Number(agentId) : null });
    setTicket(updated);
    const name = agentId ? agents.find(a => a.id === Number(agentId))?.full_name : null;
    showToast(name ? 'Ticket asignado a ' + name : 'Asignación eliminada');
  }
  async function assignSelf() {
    if (!agent?.id) return;
    const updated = await api.updateTicket(id, { assignee_id: Number(agent.id) });
    setTicket(updated);
    showToast('Ticket asignado a ti');
  }
  async function escalate() {
    if (!escalateReason.trim()) return;
    const targetLevel = escalateLevel || (ticket.level + 1);
    try {
      const res = await api.escalate({ ticketId: Number(id), reason: escalateReason, toLevel: targetLevel });
      setTicket(res.ticket);
      setShowEscalate(false); setEscalateReason(''); setEscalateLevel(null);
      showToast('Ticket escalado a N' + targetLevel);
      load();
    } catch(e) { showToast(e.message || 'Error al escalar', 'error'); }
  }
  async function deescalate() {
    if (!escalateReason.trim()) return;
    const targetLevel = escalateLevel || (ticket.level - 1);
    try {
      const updated = await api.updateTicket(id, {
        level:  targetLevel,
        status: 'open',
      });
      setTicket(updated);
      setShowEscalate(false); setEscalateReason(''); setEscalateLevel(null);
      showToast('Ticket desescalado a N' + targetLevel);
      load();
    } catch(e) { showToast(e.message || 'Error al desescalar', 'error'); }
  }
  // SQLite devuelve "YYYY-MM-DD HH:MM:SS" (UTC sin Z) — hay que normalizarlo
  function toDate(d) {
    if (!d) return null;
    const s = String(d).replace(' ', 'T');
    return new Date(s.endsWith('Z') ? s : s + 'Z');
  }

  // Fecha completa con hora: "06 abr 2026, 12:50"
  function fmtFull(d) {
    if (!d) return '—';
    const dt = toDate(d);
    return dt.toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  }

  function fmt(d) {
    if (!d) return '—';
    const dt = toDate(d);
    const diff = (Date.now() - dt) / 1000;
    if (diff < 60)    return 'ahora mismo';
    if (diff < 3600)  return 'hace ' + Math.floor(diff/60) + 'm';
    if (diff < 86400) return 'hace ' + Math.floor(diff/3600) + 'h';
    return dt.toLocaleDateString('es-ES',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});
  }

  if (loading) return <BackDeskLayout title="Ticket"><div className="bd-loading"><div className="bd-spinner-sm"/>Cargando…</div></BackDeskLayout>;
  if (!ticket) return <BackDeskLayout title="Ticket"><div className="bd-empty">Ticket no encontrado</div></BackDeskLayout>;

  const l = LEVEL_MAP[ticket.level] || LEVEL_MAP[1];
  const slaOk    = ['resolved','forwarded','scheduled'].includes(ticket.status);
  const isMine   = ticket.assignee_id === agent?.id;
  const canEscalate = !slaOk && ticket.level < 5;

  // Tiempo de resolución
  function resolutionTime() {
    if (!ticket.resolved_at || !ticket.created_at) return null;
    const ms  = toDate(ticket.resolved_at) - toDate(ticket.created_at);
    const min = Math.floor(ms / 60000);
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
  }

  return (
    <BackDeskLayout>
      {/* Toast */}
      {toast && (
        <div style={{position:'fixed',bottom:24,right:24,zIndex:9999,padding:'12px 20px',borderRadius:10,fontSize:13,fontWeight:600,
          background: toast.type === 'error' ? '#FEF2F2' : '#F0FDF4',
          color: toast.type === 'error' ? '#B91C1C' : '#15803D',
          border: '1.5px solid ' + (toast.type === 'error' ? '#FCA5A5' : '#86EFAC'),
          boxShadow:'0 4px 16px rgba(0,0,0,.12)'}}>
          {toast.type === 'error' ? '⚠ ' : '✓ '}{toast.msg}
        </div>
      )}

      {/* Modal agendar evento */}
      {showSchedule && (() => {
        const SCHED_COLORS = [
          { value:'var(--una-blue)', label:'Azul' },
          { value:'#15803D', label:'Verde' },
          { value:'#B45309', label:'Naranja' },
          { value:'#B91C1C', label:'Rojo' },
          { value:'#7C3AED', label:'Morado' },
        ];
        const inputSt = {
          width:'100%', height:36, padding:'0 10px', border:'1.5px solid #E5E7EB', borderRadius:8,
          fontSize:13, color:'#111827', outline:'none', background:'#fff', boxSizing:'border-box',
        };
        const valid = schedForm.date;
        const close = () => setShowSchedule(false);
        const confirm = async () => {
          if (!valid) return;
          setSchedSaving(true);
          try {
            await api.createEvent({
              title:       ticket.title,
              description: schedForm.description.trim(),
              start_at:    `${schedForm.date}T${schedForm.startTime}:00`,
              end_at:      `${schedForm.date}T${schedForm.endTime}:00`,
              color:       schedForm.color,
              all_day:     0,
              ticket_id:   ticket.id,
            });
            close();
            showToast('Evento agendado correctamente');
          } catch(e) {
            showToast(e.message || 'Error al agendar', 'error');
          } finally { setSchedSaving(false); }
        };
        return (
          <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(15,30,60,.5)'}}
            onClick={e => { if(e.target===e.currentTarget) close(); }}>
            <div style={{background:'#fff',borderRadius:12,width:440,maxWidth:'92vw',boxShadow:'0 8px 40px rgba(0,0,0,.14)',overflow:'hidden',border:'1px solid #E5E7EB'}}>
              <div style={{padding:'16px 20px 12px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{fontSize:15,fontWeight:700,color:'#111827'}}>Agendar evento</div>
                <button onClick={close} style={{width:28,height:28,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:14,color:'#6B7280',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
              </div>
              <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:12}}>
                {/* Ticket ref */}
                <div style={{padding:'8px 12px',background:'#E8F0FE',borderRadius:8,fontSize:12,color:'var(--una-blue)',fontWeight:600}}>
                  Vinculado a <span style={{fontFamily:'monospace'}}>{ticket.ref}</span> — {ticket.title}
                </div>
                {/* Fecha */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>
                    Fecha <span style={{color:'#E53935'}}>*</span>
                  </div>
                  <input type="date" style={inputSt}
                    value={schedForm.date} onChange={e => setSchedForm(f => ({...f, date: e.target.value}))} />
                </div>
                {/* Horas */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>Hora inicio</div>
                    <input type="time" style={inputSt}
                      value={schedForm.startTime} onChange={e => setSchedForm(f => ({...f, startTime: e.target.value}))} />
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>Hora fin</div>
                    <input type="time" style={inputSt}
                      value={schedForm.endTime} onChange={e => setSchedForm(f => ({...f, endTime: e.target.value}))} />
                  </div>
                </div>
                {/* Descripción */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:5}}>{t('ticket.description')}</div>
                  <textarea style={{...inputSt, height:64, padding:'8px 10px', resize:'none', lineHeight:1.5}}
                    placeholder="Detalles del evento…"
                    value={schedForm.description} onChange={e => setSchedForm(f => ({...f, description: e.target.value}))} />
                </div>
                {/* Color */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#6B7280',marginBottom:8}}>Color</div>
                  <div style={{display:'flex',gap:8}}>
                    {SCHED_COLORS.map(c => (
                      <button key={c.value} onClick={() => setSchedForm(f => ({...f, color: c.value}))}
                        title={c.label}
                        style={{width:24,height:24,borderRadius:'50%',background:c.value,border:'none',cursor:'pointer',
                          outline: schedForm.color === c.value ? `3px solid ${c.value}` : '3px solid transparent',
                          outlineOffset:2, transition:'outline .12s'}} />
                    ))}
                  </div>
                </div>
              </div>
              <div style={{padding:'10px 20px 16px',display:'flex',justifyContent:'flex-end',gap:8}}>
                <Button variant="secondary" onClick={close} style={{height:34}}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={confirm} disabled={!valid || schedSaving} style={{height:34}}>
                  Crear evento
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal derivar */}
      {showForward && (() => {
        const dept  = departments.find(d => d.id === Number(forwardDeptId));
        const valid = forwardDeptId && forwardReason.trim();
        const close = () => { setShowForward(false); setForwardDeptId(''); setForwardReason(''); setDeptMenuOpen(false); };
        const confirm = async () => {
          await api.updateTicket(id, { status:'forwarded', forwarded_to: Number(forwardDeptId), forward_reason: forwardReason.trim() });
          await api.addComment(id, {
            body: `Ticket derivado a: ${dept?.name}\nMotivo: ${forwardReason.trim()}`,
            author_name: agent.full_name || agent.username,
            is_internal: true,
          });
          close(); load(); showToast('Ticket derivado · se ha notificado al solicitante');
        };
        return (
          <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(15,30,60,.5)'}}
            onClick={e => { if(e.target===e.currentTarget) close(); }}>
            <div style={{background:'#fff',borderRadius:12,width:460,maxWidth:'90vw',boxShadow:'0 8px 40px rgba(0,0,0,.12)',border:'1px solid #E5E7EB'}}>
              <div style={{padding:'18px 24px 14px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'flex-start',justifyContent:'space-between',borderRadius:'12px 12px 0 0'}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:'#111827'}}>Derivar ticket</div>
                  <div style={{fontSize:12,color:'#6B7280',marginTop:3}}>
                    <span style={{fontFamily:'monospace',color:'var(--una-blue)',fontWeight:600}}>{ticket.ref}</span>
                    <span style={{margin:'0 6px',color:'#D1D5DB'}}>·</span>
                    {ticket.title}
                  </div>
                </div>
                <button onClick={close}
                  style={{width:30,height:30,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',
                    fontSize:14,color:'#6B7280',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
              </div>
              <div style={{padding:'20px 24px 16px',display:'flex',flexDirection:'column',gap:14}}>
                <div style={{position:'relative'}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#5B8DB8',marginBottom:8}}>
                    Departamento de destino <span style={{color:'#E53935'}}>*</span>
                  </div>
                  {/* Custom dropdown — no desborda el modal */}
                  <button type="button"
                    onClick={() => setDeptMenuOpen(o => !o)}
                    style={{width:'100%',height:38,padding:'0 10px',border:'1.5px solid '+(deptMenuOpen?'var(--una-blue)':'#C5D8E8'),
                      borderRadius:8,fontSize:13,color:forwardDeptId?'#111827':'#9CA3AF',
                      background:'#F8FBFD',cursor:'pointer',display:'flex',alignItems:'center',
                      justifyContent:'space-between',textAlign:'left',outline:'none',boxSizing:'border-box'}}>
                    <span>{dept ? dept.name : '— Selecciona un departamento —'}</span>
                    <span style={{fontSize:10,color:'#9CA3AF',marginLeft:8}}>▼</span>
                  </button>
                  {deptMenuOpen && (
                    <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:10,
                      background:'#fff',border:'1.5px solid #C5D8E8',borderRadius:8,
                      boxShadow:'0 4px 16px rgba(0,0,0,.1)',overflow:'hidden'}}>
                      {departments.map(d => (
                        <button key={d.id} type="button"
                          onClick={() => { setForwardDeptId(String(d.id)); setDeptMenuOpen(false); }}
                          style={{width:'100%',padding:'10px 14px',background:forwardDeptId===String(d.id)?'#E8F0FE':'#fff',
                            border:'none',cursor:'pointer',fontSize:13,color:'#111827',
                            textAlign:'left',display:'block',
                            borderBottom:'1px solid #F3F4F6'}}>
                          {d.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#5B8DB8',marginBottom:8}}>
                    Motivo <span style={{color:'#E53935'}}>*</span>
                  </div>
                  <textarea
                    style={{width:'100%',height:88,padding:'10px 12px',border:'1.5px solid #C5D8E8',borderRadius:8,
                      fontSize:13,lineHeight:1.6,color:'#111827',outline:'none',resize:'none',
                      boxSizing:'border-box',fontFamily:'var(--font-body)',background:'#F8FBFD',transition:'border-color .15s'}}
                    placeholder="Explica por qué se deriva este ticket…"
                    value={forwardReason} onChange={e => setForwardReason(e.target.value)}
                    onFocus={e => e.target.style.borderColor='var(--una-blue)'}
                    onBlur={e => e.target.style.borderColor='#C5D8E8'}
                  />
                </div>
              </div>
              <div style={{padding:'12px 24px 16px',display:'flex',justifyContent:'flex-end',gap:8}}>
                <Button variant="secondary" onClick={close} style={{height:38}}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={confirm} disabled={!valid} style={{height:38}}>
                  <MIcon name="forward" size={14}/>
                  Confirmar derivación
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Título + chips + acciones */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,marginBottom:20}}>
        <div style={{flex:1,minWidth:0}}>
          <h1 style={{fontSize:22,fontWeight:700,color:'#111827',letterSpacing:'-.3px',margin:'0 0 10px'}}>{ticket.title}</h1>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <StatusChip status={ticket.status}/>
            <PrioChip priority={ticket.priority}/>
            <LevelChip level={ticket.level}/>
          </div>
        </div>
        <div style={{display:'flex',gap:8,flexShrink:0,paddingTop:4}}>
          {(agent?.role === 'admin' || agent?.role === 'superadmin') && (
            <Button variant="danger" onClick={handleDelete}>
              <MIcon name="delete" size={14}/>
              Eliminar
            </Button>
          )}
          <Button variant="ghost"
            onClick={() => {
              const today = new Date();
              const pad = n => String(n).padStart(2,'0');
              setSchedForm({ date: `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`, startTime:'09:00', endTime:'10:00', description:'', color:'var(--una-blue)' });
              setShowSchedule(true);
            }}>
            <MIcon name="calendar_month" size={14}/>
            Agendar
          </Button>
          {!slaOk && (
            <Button variant="ghost"
              onClick={() => { setForwardReason(''); setShowForward(true); }}>
              <MIcon name="forward" size={14}/>
              Derivar
            </Button>
          )}
          {(canEscalate || ticket.level > 1) && !slaOk && (
            <Button variant="ghost"
              onClick={() => { setEscalateLevel(null); setEscalateReason(''); setShowEscalate(true); }}>
              <MIcon name="swap_vert" size={14}/>
              Cambiar nivel
            </Button>
          )}
          {!slaOk && (
            <Button variant="primary" onClick={() => changeStatus('resolved')}>
              <MIcon name="check_circle" size={14}/>
              Resolver
            </Button>
          )}
        </div>
      </div>

      {/* Modal cambio de nivel */}
      {showEscalate && (() => {
        const selLv   = escalateLevel;
        const isDown  = selLv !== null && selLv < ticket.level;
        const accent  = selLv === null ? '#9CA3AF' : 'var(--una-blue)';
        const valid   = selLv !== null && escalateReason.trim();
        const close   = () => { setShowEscalate(false); setEscalateReason(''); setEscalateLevel(null); };
        const confirm = () => isDown ? deescalate() : escalate();
        return (
          <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(15,30,60,.5)'}}
            onClick={e => { if(e.target===e.currentTarget) close(); }}>
            <div style={{background:'#fff',borderRadius:12,width:460,maxWidth:'90vw',boxShadow:'0 8px 40px rgba(0,0,0,.12)',overflow:'hidden',border:'1px solid #E5E7EB'}}>
              {/* Header */}
              <div style={{padding:'18px 24px 14px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:'#111827'}}>{t('ticket.change_level')}</div>
                  <div style={{fontSize:12,color:'#6B7280',marginTop:3}}>
                    Nivel actual: <strong style={{color:l.color,fontWeight:700}}>N{ticket.level}</strong>
                    <span style={{margin:'0 6px',color:'#D1D5DB'}}>·</span>
                    <span style={{fontFamily:'monospace',color:'var(--una-blue)',fontWeight:600}}>{ticket.ref}</span>
                  </div>
                </div>
                <button onClick={close}
                  style={{width:30,height:30,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',
                    fontSize:14,color:'#6B7280',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
              </div>
              {/* Body */}
              <div style={{padding:'20px 24px 16px'}}>
                {/* Selector de nivel */}
                <div style={{marginBottom:18}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#5B8DB8',marginBottom:10}}>
                    Nivel destino
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    {[1,2,3,4,5].filter(lv => lv !== ticket.level).map(lv => {
                      const lc     = LEVEL_MAP[lv];
                      const sel    = selLv === lv;
                      const goDown = lv < ticket.level;
                      const selColor = 'var(--una-blue)';
                      const selBg    = 'var(--una-blue-lt)';
                      return (
                        <button key={lv} onClick={() => setEscalateLevel(lv)}
                          style={{flex:1,height:56,borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer',
                            background: sel ? selBg : '#fff',
                            color:      sel ? selColor : '#9CA3AF',
                            border:'2px solid ' + (sel ? selColor : '#E5E7EB'),
                            transition:'all .15s',
                            display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2}}>
                          <span>N{lv}</span>
                          <span style={{fontSize:9,fontWeight:500,textTransform:'uppercase',letterSpacing:.4,
                            opacity: sel ? .85 : .5, color: sel ? selColor : '#9CA3AF'}}>
                            {goDown ? '↓ bajar' : '↑ subir'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Motivo */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#5B8DB8',marginBottom:8}}>
                    Motivo <span style={{color:'#E53935'}}>*</span>
                  </div>
                  <textarea
                    style={{width:'100%',height:88,padding:'10px 12px',border:'1.5px solid #C5D8E8',borderRadius:8,
                      fontSize:13,lineHeight:1.6,color:'#111827',outline:'none',resize:'none',
                      boxSizing:'border-box',fontFamily:'var(--font-body)',background:'#F8FBFD',transition:'border-color .15s'}}
                    placeholder="Explica el motivo del cambio de nivel…"
                    value={escalateReason} onChange={e => setEscalateReason(e.target.value)}
                    autoFocus
                    onFocus={e => e.target.style.borderColor='var(--una-blue)'}
                    onBlur={e => e.target.style.borderColor='#C5D8E8'}
                  />
                </div>
              </div>
              {/* Footer */}
              <div style={{padding:'12px 24px 16px',display:'flex',justifyContent:'flex-end',gap:8}}>
                <Button variant="secondary" onClick={close} style={{height:38}}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={confirm} disabled={!valid} style={{height:38}}>
                  <MIcon name={selLv === null ? 'swap_vert' : isDown ? 'arrow_downward' : 'arrow_upward'} size={14}/>
                  {selLv === null ? 'Selecciona un nivel' : isDown ? `Desescalar a N${selLv}` : `Escalar a N${selLv}`}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:20,alignItems:'start'}}>

        {/* ── Columna principal ─────────────────────────────────── */}
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Información */}
          <Card title={t('ticket.info_card')}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:16}}>
              {!editingContact ? (
                <>
                  <div>
                    <div className="bd-field-label">{t('ticket.requester')}</div>
                    <div style={{fontSize:13,color:'#111827',fontWeight:500,display:'flex',alignItems:'center',gap:5}}>
                      <MIcon name="person" size={14} color="#9CA3AF"/>
                      {ticket.requester}
                    </div>
                  </div>
                  {ticket.requester_email && (
                    <div>
                      <div className="bd-field-label">Email</div>
                      <a href={'mailto:' + ticket.requester_email} style={{fontSize:13,color:'var(--una-blue)',fontWeight:500,display:'flex',alignItems:'center',gap:5}}>
                        <MIcon name="mail" size={14} color="#9CA3AF"/>
                        {ticket.requester_email}
                      </a>
                    </div>
                  )}
                  {ticket.watchers?.length > 0 && (
                    <div>
                      <div className="bd-field-label">En copia</div>
                      <div style={{fontSize:13,color:'#374151',display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                        <MIcon name="group" size={14} color="#9CA3AF"/>
                        {ticket.watchers.join(', ')}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setContactForm({
                        requester: ticket.requester || '',
                        requester_email: ticket.requester_email || '',
                        watcher_emails: (ticket.watchers || []).join(', '),
                      });
                      setEditingContact(true);
                    }}
                    style={{marginLeft:'auto',background:'none',border:'1px solid #E5E7EB',borderRadius:6,width:28,height:28,cursor:'pointer',color:'#9CA3AF',display:'flex',alignItems:'center',justifyContent:'center'}}
                    title="Editar solicitante, email y copia">
                    <MIcon name="edit" size={14}/>
                  </button>
                </>
              ) : (
                <div style={{width:'100%',display:'flex',flexDirection:'column',gap:10}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div>
                      <div className="bd-field-label" style={{marginBottom:4}}>{t('ticket.requester')}</div>
                      <input value={contactForm.requester}
                        onChange={e => setContactForm(f => ({...f, requester: e.target.value}))}
                        style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
                    </div>
                    <div>
                      <div className="bd-field-label" style={{marginBottom:4}}>Email</div>
                      <input value={contactForm.requester_email} type="email"
                        onChange={e => setContactForm(f => ({...f, requester_email: e.target.value}))}
                        style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
                    </div>
                  </div>
                  <div>
                    <WatcherEmailsField value={contactForm.watcher_emails} onChange={v => setContactForm(f => ({...f, watcher_emails: v}))} />
                  </div>
                  <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <Button variant="secondary" size="sm" onClick={() => setEditingContact(false)}>
                      Cancelar
                    </Button>
                    <Button
                      variant="primary" size="sm"
                      onClick={() => saveContactInfo({
                        requester: contactForm.requester.trim(),
                        requester_email: contactForm.requester_email.trim(),
                        watcher_emails: contactForm.watcher_emails.split(',').map(e => e.trim()).filter(Boolean),
                      })}>
                      Guardar
                    </Button>
                  </div>
                </div>
              )}
              <div>
                <div className="bd-field-label">{t('ticket.reference')}</div>
                <div style={{fontSize:13,color:'var(--una-blue)',fontFamily:'monospace',fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
                  <MIcon name="tag" size={14} color="#9CA3AF"/>
                  {ticket.ref}
                </div>
              </div>
              <div>
                <div className="bd-field-label">{t('ticket.created_at')}</div>
                <div style={{fontSize:13,color:'#374151',display:'flex',alignItems:'center',gap:5}}>
                  <MIcon name="calendar_today" size={14} color="#9CA3AF"/>
                  {fmtFull(ticket.created_at)}
                </div>
              </div>
              {resolutionTime() && (
                <div>
                  <div className="bd-field-label">{t('ticket.resolve_time')}</div>
                  <div style={{fontSize:13,color:'#15803D',fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
                    <MIcon name="timer" size={14} color="#9CA3AF"/>
                    {resolutionTime()}
                  </div>
                </div>
              )}
              {ticket.resolved_at && ticket.status === 'resolved' && (
                <div>
                  <div className="bd-field-label">{t('status.resolved')}</div>
                  <div style={{fontSize:13,color:'#15803D',fontWeight:500,display:'flex',alignItems:'center',gap:5}}>
                    <MIcon name="check_circle" size={14} color="#9CA3AF"/>
                    {fmtFull(ticket.resolved_at)}
                  </div>
                </div>
              )}
            </div>
            {ticket.description && (
              <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid #F3F4F6'}}>
                <div className="bd-field-label" style={{marginBottom:6}}>{t('ticket.description')}</div>
                <p style={{fontSize:14,color:'#4B5563',lineHeight:1.7,margin:0,whiteSpace:'pre-wrap'}}>{ticket.description}</p>
              </div>
            )}
            {ticket.extra_fields && Object.keys(ticket.extra_fields).length > 0 && (
              <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid #F3F4F6'}}>
                <div className="bd-field-label" style={{marginBottom:10}}>{t('ticket.extra_info')}</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12}}>
                  {Object.entries(ticket.extra_fields).map(([k,v]) => v && (
                    <div key={k}>
                      <div className="bd-field-label">{k.replace(/_/g,' ')}</div>
                      <div style={{fontSize:13,color:'#111827',fontWeight:500}}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {attachments.filter(a => !a.comment_id).length > 0 && (
            <Card title={t('ticket.add_files')}>
              <AttachGallery attachments={attachments.filter(a => !a.comment_id)}/>
            </Card>
          )}

          <Card noPad>
            <div style={{display:'flex',borderBottom:'2px solid #E5E7EB',background:'#fff',borderRadius:'12px 12px 0 0'}}>
              {[
                {key:'comments', label:t('ticket.comments_tab') + ' (' + comments.length + ')', icon:'chat'},
                SHOW_DEVICE_INFO && {key:'device',   label:t('ticket.device_tab'), icon:'computer', disabled: !ticket.device_info},
                {key:'history',  label:t('ticket.history_tab'), icon:'history'},
              ].filter(Boolean).map(tab => (
                <button key={tab.key}
                  disabled={tab.disabled}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    display:'flex',alignItems:'center',gap:6,
                    padding:'0 20px',height:44,
                    fontSize:11,fontWeight:700,letterSpacing:.6,textTransform:'uppercase',
                    background:'transparent',border:'none',
                    cursor: tab.disabled ? 'not-allowed' : 'pointer',
                    borderBottom: activeTab === tab.key ? '2px solid var(--una-blue)' : '2px solid transparent',
                    marginBottom:'-2px',
                    color: tab.disabled ? '#D1D5DB' : activeTab === tab.key ? 'var(--una-blue)' : '#9CA3AF',
                    transition:'color .15s, border-color .15s',
                  }}>
                  <MIcon name={tab.icon} size={15} style={{opacity: tab.disabled ? .4 : 1}}/>
                  {tab.label}
                </button>
              ))}
            </div>

            {SHOW_DEVICE_INFO && activeTab === 'device' && (
              <div style={{padding:18}}>
                <DevicePanel deviceInfo={ticket.device_info} agentAvailable={!!ticket.device_info}/>
              </div>
            )}

            {activeTab === 'comments' && (
              <div style={{padding:18}}>
                {comments.length === 0
                  ? <div style={{textAlign:'center',padding:'24px 0',color:'#9CA3AF',fontSize:13}}>Sin comentarios aún</div>
                  : (
                    <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:16}}>
                      {comments.map(c => (
                        <div key={c.id} style={{padding:'12px 14px',borderRadius:10,
                          background: c.is_internal ? '#FFFBEB' : '#F8FAFF',
                          borderLeft:'3px solid ' + (c.is_internal ? '#FDE68A' : '#BFDBFE')}}>
                          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                            <div style={{width:28,height:28,borderRadius:'50%',background:c.is_internal?'#F59E0B':'var(--una-blue)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>
                              {c.author_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span style={{fontSize:12,fontWeight:600,color:'#111827'}}>{c.author_name}</span>
                            {c.is_internal
                              ? <span style={{fontSize:10,padding:'1px 7px',background:'#FEF3C7',color:'#B45309',borderRadius:8,border:'1px solid #FDE68A'}}>{t('ticket.internal')}</span>
                              : <span style={{fontSize:10,padding:'1px 7px',background:'#DBEAFE',color:'#1D4ED8',borderRadius:8,border:'1px solid #BFDBFE'}}>{t('ticket.public')}</span>
                            }
                            <span style={{fontSize:11,color:'#9CA3AF',marginLeft:'auto'}}>{fmt(c.created_at)}</span>
                          </div>
                          <div style={{fontSize:13,lineHeight:1.65,color:'#374151',paddingLeft:36,whiteSpace:'pre-wrap'}}>{c.body}</div>
                        {attachments.filter(a => a.comment_id === c.id).length > 0 && (
                          <div style={{paddingLeft:36,marginTop:6}}>
                            <AttachGallery attachments={attachments.filter(a => a.comment_id === c.id)}/>
                          </div>
                        )}
                        </div>
                      ))}
                    </div>
                  )
                }
                <div style={{display:'flex',borderBottom:'2px solid #E5E7EB',marginBottom:12}}>
                  <button onClick={() => setIsInternal(false)}
                    style={{
                      flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,
                      padding:'0 12px',height:38,fontSize:12,fontWeight:700,
                      background:'transparent',border:'none',cursor:'pointer',
                      borderBottom: !isInternal ? '2px solid var(--una-blue)' : '2px solid transparent',
                      marginBottom:'-2px',
                      color: !isInternal ? 'var(--una-blue)' : '#9CA3AF',
                      transition:'color .15s, border-color .15s',
                    }}>
                    <MIcon name="public" size={14}/>
                    {t('ticket.public_note')}
                  </button>
                  <button onClick={() => setIsInternal(true)}
                    style={{
                      flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,
                      padding:'0 12px',height:38,fontSize:12,fontWeight:700,
                      background:'transparent',border:'none',cursor:'pointer',
                      borderBottom: isInternal ? '2px solid #F59E0B' : '2px solid transparent',
                      marginBottom:'-2px',
                      color: isInternal ? '#B45309' : '#9CA3AF',
                      transition:'color .15s, border-color .15s',
                    }}>
                    <MIcon name="lock" size={14}/>
                    {t('ticket.internal_note2')}
                  </button>
                </div>
                <CommentEditor
                  isInternal={isInternal}
                  value={comment} onChange={setComment}
                  files={attachFiles} onFilesChange={setAttachFiles}
                  sending={sending} onSend={sendComment}
                 t={t}/>
              </div>
            )}

            {activeTab === 'history' && (
              <div style={{padding:18}}>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <HistoryItem icon="📋" text={'Ticket creado por ' + ticket.requester} time={ticket.created_at} fmt={fmt}/>
                  {ticket.assignee_name && <HistoryItem icon="👤" text={'Asignado a ' + ticket.assignee_name} time={ticket.updated_at} fmt={fmt}/>}
                  {ticket.level > 1 && <HistoryItem icon="⬆️" text={t('ticket.escalated_to') + ticket.level} time={ticket.updated_at} fmt={fmt}/>}
                  {ticket.status === 'resolved' && <HistoryItem icon="✅" text="Ticket marcado como resuelto" time={ticket.resolved_at} fmt={fmt} highlight/>}
                  {[...comments].sort((a,b) => new Date(a.created_at)-new Date(b.created_at)).map(c => (
                    <HistoryItem key={c.id}
                      icon={c.is_internal ? '🔒' : '💬'}
                      text={(c.is_internal ? t('ticket.internal') : t('ticket.public')) + ' · ' + c.author_name}
                      time={c.created_at} fmt={fmt} muted={c.is_internal}/>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* ── Sidebar derecha ───────────────────────────────────── */}
        <div style={{display:'flex',flexDirection:'column',gap:14}}>

          <Card title={t('ticket.sla_card')}>
            <SLAIndicator ticket={ticket} t={t}/>
          </Card>

          <Card title={t('ticket.assignment')}>
            <div className="bd-field-label" style={{marginBottom:6}}>{t('ticket.agent_assigned')}</div>
            <select value={ticket.assignee_id||''} onChange={e => assign(e.target.value||null)}
              style={{width:'100%',height:36,padding:'0 10px',background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,color:'#111827',fontSize:13,outline:'none',marginBottom:8}}>
              <option value="">Sin asignar</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>{a.full_name}{a.id === agent?.id ? ' (Yo)' : ''} — N{a.max_level}</option>
              ))}
            </select>
            {ticket.assignee_id !== agent?.id ? (
              <button onClick={assignSelf}
                style={{width:'100%',height:34,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',
                  background:'#E8F5E9',color:'#1E8E3E',border:'none',
                  display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                <MIcon name="person_add" size={14}/>
                Asignarme este ticket
              </button>
            ) : (
              <div style={{padding:'6px 10px',borderRadius:8,background:'#E8F5E9',fontSize:12,color:'#1E8E3E',fontWeight:600,
                textAlign:'center',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                <MIcon name="check_circle" size={14}/>
                Asignado a ti
              </div>
            )}
          </Card>

          <Card title={t('ticket.status')}>
            <select value={ticket.status} onChange={e => changeStatus(e.target.value)}
              style={{width:'100%',height:36,padding:'0 10px',background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,color:'#111827',fontSize:13,outline:'none'}}>
              {Object.entries(STATUS_MAP).map(([k,v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Card>

          <Card title={t('ticket.priority')}>
            <select value={ticket.priority} onChange={e => changePriority(e.target.value)}
              style={{width:'100%',height:36,padding:'0 10px',background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,color:'#111827',fontSize:13,outline:'none'}}>
              {Object.entries(PRIO_MAP).map(([k,v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Card>

        </div>
      </div>
    </BackDeskLayout>
  );
}

function HistoryItem({ icon, text, time, fmt, highlight, muted }) {
  return (
    <div style={{display:'flex',gap:10,alignItems:'flex-start',padding:'8px 10px',borderRadius:8,
      background: highlight ? '#F0FDF4' : muted ? '#FAFAFA' : '#fff',
      border:'1px solid ' + (highlight ? '#86EFAC' : '#F3F4F6')}}>
      <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
      <div style={{flex:1}}>
        <div style={{fontSize:12,color: muted ? '#9CA3AF' : '#374151',fontWeight: highlight ? 600 : 400}}>{text}</div>
        <div style={{fontSize:11,color:'#9CA3AF',marginTop:2}}>{fmt(time)}</div>
      </div>
    </div>
  );
}

// ── CommentEditor — textarea enriquecido con barra de iconos ─────────────
function CommentEditor({  isInternal, value, onChange, files, onFilesChange, sending, onSend , t }) {
  const fileRef = React.useRef();
  const accentBorder = isInternal ? '#FDE68A' : '#BFDBFE';
  const accentBg     = isInternal ? '#FFFBEB' : '#F8FAFF';
  const accentText   = isInternal ? '#B45309' : '#1D4ED8';
  const btnBg        = isInternal ? '#F59E0B' : 'var(--una-blue)';

  function addFiles(incoming) {
    const combined = [...(files||[])];
    for (const f of incoming) {
      if (combined.length >= 3) break;
      if (!combined.find(x => x.name===f.name && x.size===f.size)) combined.push(f);
    }
    onFilesChange(combined);
  }

  function removeFile(i) {
    const next = [...files]; next.splice(i,1); onFilesChange(next);
  }

  function fmtSize(b) {
    if (b < 1024) return b+'B';
    if (b < 1048576) return Math.round(b/1024)+'KB';
    return (b/1048576).toFixed(1)+'MB';
  }

  const isImg = f => f.type?.startsWith('image/');

  return (
    <div style={{border:'1.5px solid '+accentBorder,borderRadius:10,overflow:'hidden',background:accentBg}}>
      {/* Label */}
      <div style={{padding:'5px 12px',fontSize:11,fontWeight:600,color:accentText,borderBottom:'1px solid '+accentBorder}}>
        {isInternal ? t('ticket.internal_comment') : t('ticket.public_comment')}
      </div>

      {/* Textarea */}
      <textarea
        style={{width:'100%',minHeight:72,padding:'8px 12px',background:'transparent',border:'none',
          outline:'none',fontSize:13,lineHeight:1.65,color:'#111827',resize:'none',
          fontFamily:'var(--font-body)',boxSizing:'border-box'}}
        placeholder={isInternal ? t('ticket.write_internal') : t('ticket.write_comment')}
        value={value} onChange={e => onChange(e.target.value)}
      />

      {/* Chips de archivos seleccionados */}
      {files?.length > 0 && (
        <div style={{padding:'4px 12px 6px',display:'flex',flexWrap:'wrap',gap:6,borderTop:'1px solid '+accentBorder}}>
          {files.map((f,i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:5,height:24,padding:'0 6px 0 5px',
              borderRadius:6,background:'#fff',border:'1px solid #C5D8E8',maxWidth:180}}>
              {isImg(f)
                ? <img src={URL.createObjectURL(f)} alt="" style={{width:16,height:16,borderRadius:2,objectFit:'cover',flexShrink:0}}/>
                : <MIcon name="insert_drive_file" size={13} style={{color:'var(--una-blue)',flexShrink:0}}/>
              }
              <span style={{fontSize:11,fontWeight:600,color:'#374151',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:100}}>
                {f.name}
              </span>
              <span style={{fontSize:10,color:'#9CA3AF',flexShrink:0}}>{fmtSize(f.size)}</span>
              <button onClick={()=>removeFile(i)}
                style={{width:14,height:14,borderRadius:3,border:'none',background:'#E0EEF7',cursor:'pointer',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#5B8DB8',flexShrink:0,padding:0}}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Footer toolbar */}
      <div style={{padding:'4px 8px',borderTop:'1px solid '+accentBorder,display:'flex',alignItems:'center',gap:4}}>
        {/* Adjuntar */}
        <button title="Adjuntar archivo" onClick={()=>fileRef.current.click()}
          style={{width:30,height:30,borderRadius:6,border:'none',background:'transparent',cursor:'pointer',
            display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF',transition:'all .12s'}}
          onMouseOver={e=>e.currentTarget.style.background='#E0EEF7'}
          onMouseOut={e=>e.currentTarget.style.background='transparent'}>
          <MIcon name="attach_file" size={17}/>
        </button>
        {/* Imagen */}
        <button title="Adjuntar imagen" onClick={()=>{ fileRef.current.accept='image/*'; fileRef.current.click(); setTimeout(()=>fileRef.current.accept='image/*,.pdf,.doc,.docx,.txt',100); }}
          style={{width:30,height:30,borderRadius:6,border:'none',background:'transparent',cursor:'pointer',
            display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF',transition:'all .12s'}}
          onMouseOver={e=>e.currentTarget.style.background='#E0EEF7'}
          onMouseOut={e=>e.currentTarget.style.background='transparent'}>
          <MIcon name="image" size={17}/>
        </button>
        {files?.length > 0 && (
          <span style={{fontSize:10,color:'#9CA3AF',marginLeft:2}}>{files.length}/3</span>
        )}
        <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt"
          style={{display:'none'}} onChange={e=>{ addFiles([...e.target.files]); e.target.value=''; }}/>

        {/* Spacer */}
        <div style={{flex:1}}/>

        {/* Enviar */}
        <button onClick={onSend} disabled={sending || !value.trim()}
          style={{height:30,padding:'0 16px',borderRadius:7,fontSize:12,fontWeight:700,border:'none',
            cursor:sending||!value.trim()?'not-allowed':'pointer',
            opacity:sending||!value.trim()?0.45:1,
            background:btnBg,color:'#fff',display:'flex',alignItems:'center',gap:6,transition:'opacity .15s'}}>
          <MIcon name={isInternal?'lock':'send'} size={13}/>
          {sending ? 'Enviando…' : isInternal ? 'Guardar nota' : 'Enviar'}
        </button>
      </div>
    </div>
  );
}


function toUtcDate(d) {
  if (!d) return null;
  const s = String(d).replace(' ', 'T');
  return new Date(s.endsWith('Z') ? s : s + 'Z');
}

function SLAIndicator({  ticket , t }) {
  const SLA_MINUTES = { critical:15, high:60, medium:240, low:480 };
  const slaMin = SLA_MINUTES[ticket.priority] || 240;
  const elapsed = (Date.now() - toUtcDate(ticket.created_at)) / 60000;
  const pct = Math.min(100, Math.round((elapsed / slaMin) * 100));
  const resolved = ['resolved','forwarded','scheduled'].includes(ticket.status);
  const over = !resolved && pct >= 100;
  const color = resolved ? '#15803D' : over ? '#B91C1C' : pct > 70 ? '#B45309' : 'var(--una-blue)';
  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
        <span style={{fontSize:12,color:'#6B7280'}}>
          {resolved ? t('ticket.sla_ok') : over ? t('ticket.sla_over') : Math.round(elapsed) + 'm de ' + slaMin + 'm'}
        </span>
        <span style={{fontSize:12,fontWeight:600,color}}>{resolved ? '✓' : pct + '%'}</span>
      </div>
      <div style={{height:6,background:'#F3F4F6',borderRadius:3,overflow:'hidden'}}>
        <div style={{height:'100%',width:pct+'%',background:color,borderRadius:3,transition:'width .3s'}}/>
      </div>
      <div style={{marginTop:8,fontSize:11,color:'#9CA3AF'}}>
        {t ? t('ticket.priority') : 'Prioridad'} {t ? t('priority.' + (ticket.priority || 'low')) : (ticket.priority || '')} · SLA {slaMin} min
      </div>
    </div>
  );
}
